"use client";
import { ReservationGuideDialog } from "@/components/medical-checkups/ReservationGuideDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  type FacilityDescription,
  type FacilityOption,
  type FacilityPlan,
  type HealthCheckupFacilityContract,
  type MedicalCheckupOption,
  type MedicalCheckupPlan,
  type MedicalCheckupSegment,
  calculatePlanCounterPayAmount,
  calculatePlanSelfPayAmount,
} from "@/server/models/medical-checkup/medical-checkups";
import Link from "next/link";
import { useId, useState } from "react";
import {
  MdCheck,
  MdCheckCircle,
  MdChevronRight,
  MdClose,
  MdExpandMore,
  MdLocationOn,
} from "react-icons/md";
import { OptionRowWithCalculation } from "./OptionRowWithCalculation";
import { PlanRow } from "./PlanRow";
import type { OptionSubsidy, PlanSubsidy } from "./types";

/**
 * 健診機関表示の共通コンポーネント
 * 加入者・被扶養者の健診機関一覧で共通利用
 */
export const FacilityCard = ({
  fiscalYear,
  name,
  address,
  description,
  segment,
  plans,
  options,
  reservationUrl,
  reservationTel,
  url,
  planSubsidies,
  optionSubsidies,
  allOptions,
  mapPlaceId,
  contract,
  currentMember,
  reservationFormUrl,
  isRegularInsured = false,
}: {
  fiscalYear: number;
  name: string;
  address: string | null;
  description: FacilityDescription;
  segment: MedicalCheckupSegment;
  plans: FacilityPlan[];
  options: FacilityOption[];
  reservationUrl: string | null;
  reservationTel: string | null;
  url: string | null;
  planSubsidies: Partial<Record<MedicalCheckupPlan, PlanSubsidy>>;
  optionSubsidies?: Partial<Record<MedicalCheckupOption, OptionSubsidy>>;
  allOptions?: Record<MedicalCheckupOption, { segment: MedicalCheckupSegment }>;
  mapPlaceId: string | null;
  contract?: HealthCheckupFacilityContract;
  currentMember: {
    sex: "male" | "female" | null;
    relationshipToPrimary: string | null;
  };
  reservationFormUrl: string;
  isRegularInsured?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  // オプション検査の折りたたみ状態
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsPanelId = useId();
  const handleReservationGuideClick = () => {
    setOpen(true);
  };
  // 特徴リスト
  const features = [
    {
      name: "外国語対応",
      available: description.multiLanguage,
    },
    {
      name: "バリアフリー対応",
      available: description.barrierFree,
    },
  ];
  // 女性専用・男性専用は segmentの判定がtrueになる時のみ表示する
  if (segment === "female") {
    features.push({ name: "女性専用", available: "Yes" as const });
  }
  if (segment === "male") {
    features.push({ name: "男性専用", available: "Yes" as const });
  }

  let query = `&query=${encodeURIComponent(name)}`;
  if (address) {
    query += `,${encodeURIComponent(address)}`;
  }
  let query_place_id = "";
  if (mapPlaceId) {
    query_place_id = `&query_place_id=${mapPlaceId}`;
  }
  const googleMapApiUrl = `https://www.google.com/maps/search/?api=1${query}${query_place_id}`;

  // 表示対象のオプションをフィルタリング
  // options はサーバ側で display_order 順に整列済みのため並べ替えは不要
  const filteredOptions = options.filter(
    (option) =>
      allOptions === undefined ||
      allOptions[option.code]?.segment === "general" ||
      allOptions[option.code]?.segment === currentMember.sex,
  );

  // 補助対象のプランをフィルタリング
  // plans はサーバ側で display_order 順に整列済みのため並べ替えは不要
  const filteredPlans = plans.filter(
    (plan) => planSubsidies[plan.code] != null,
  );

  return (
    <>
      {/* 全体をグレー背景で囲む */}
      <div className="rounded-lg bg-monotone-1 p-4 md:p-6">
        {/* ヘッダー部分：施設名、契約タイプ、特徴、予約ボタン */}
        <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-bold">{name}</h2>
              {contract && contract !== "none" && (
                <Badge className="rounded-full bg-primary-1 px-3 py-1 text-xs font-bold text-primary-6 hover:bg-primary-1">
                  {contract === "direct" ? "直契約" : "東振協契約"}
                </Badge>
              )}
            </div>
            <p className="mt-2 text-sm text-monotone-7">
              {description.content}
            </p>
            {address && (
              <p className="mt-2 text-sm">
                <Link
                  href={googleMapApiUrl}
                  className="flex items-center gap-1 text-primary-6"
                  target="_blank"
                  prefetch={false}
                >
                  <MdLocationOn className="h-4 w-4" />
                  {address}
                </Link>
              </p>
            )}
          </div>

          <div className="flex flex-col items-end gap-3">
            {/* 特徴（チェックマーク/Xマーク付き） */}
            {features.filter((f) => f.available !== "Unknown").length > 0 && (
              <div className="flex flex-wrap justify-end gap-3">
                {features
                  .filter((feature) => feature.available !== "Unknown")
                  .map((feature) => (
                    <div
                      key={feature.name}
                      className="flex items-center gap-1 text-sm text-monotone-7"
                    >
                      {feature.available === "Yes" ? (
                        <MdCheck className="h-4 w-4 text-primary-6" />
                      ) : (
                        <MdClose className="h-4 w-4 text-error" />
                      )}
                      <span className="font-bold">{feature.name}</span>
                    </div>
                  ))}
              </div>
            )}

            {/* 予約案内ボタン */}
            <Button
              variant="primary"
              className="flex items-center gap-2 px-6"
              onClick={() => handleReservationGuideClick()}
            >
              予約案内
              <MdChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* 健診コース（それぞれ白背景の角丸ボックス） */}
        <div className="space-y-3">
          {filteredPlans.map((plan) => {
            const planSubsidy = planSubsidies[plan.code];
            const selfPayAmount =
              planSubsidy != null
                ? calculatePlanSelfPayAmount({
                    businessBurdenAmount: planSubsidy.business_burden_amount,
                    subsidyAmount: planSubsidy.max_amount_of_subsidy,
                    relationshipToPrimary:
                      currentMember.relationshipToPrimary ?? null,
                    priceRange: { min: plan.price, max: plan.price },
                  }).min
                : plan.price;

            // 窓口支払額の計算
            const counterPayAmount = calculatePlanCounterPayAmount({
              contract: contract ?? "none",
              selfPayAmount,
              basePrice: plan.price,
              subsidyAmount: planSubsidy?.max_amount_of_subsidy ?? 0,
              businessBurdenAmount: planSubsidy?.business_burden_amount ?? 0,
            });

            return (
              <div
                key={plan.code}
                className="rounded-lg bg-white px-4 py-4 md:px-6"
              >
                <PlanRow
                  label={plan.label}
                  description={plan.description}
                  selfPayAmount={selfPayAmount}
                  counterPayAmount={counterPayAmount}
                  businessBurdenAmount={
                    isRegularInsured
                      ? planSubsidy?.business_burden_amount
                      : undefined
                  }
                />
              </div>
            );
          })}
        </div>

        {/* 対応オプション検査（白背景の角丸ボックス・折りたたみ） */}
        {filteredOptions.length > 0 && (
          <div className="mt-4 rounded-lg bg-white">
            <button
              type="button"
              aria-expanded={optionsOpen}
              aria-controls={optionsPanelId}
              className="flex w-full items-center justify-between rounded-lg px-4 py-4 text-left hover:bg-monotone-0.5 md:px-6"
              onClick={() => setOptionsOpen(!optionsOpen)}
            >
              <div className="flex items-center gap-2">
                <MdCheckCircle className="h-5 w-5 fill-monotone-6 text-white" />
                <span className="font-bold">オプション検査</span>
              </div>
              <MdExpandMore
                className={cn(
                  "h-5 w-5 text-monotone-6 transition-transform duration-200",
                  optionsOpen && "rotate-180",
                )}
              />
            </button>

            <div
              id={optionsPanelId}
              className={cn(
                "overflow-hidden transition-all duration-300",
                optionsOpen ? "max-h-[9999px]" : "max-h-0",
              )}
            >
              <div className="border-t border-monotone-2 px-4 py-4 md:px-6">
                <p className="mb-4 text-sm">
                  ※
                  補助対象の内容は対象者の年度末年齢により異なります。補助対象外の項目については、健診機関が定めた料金が適用されます。
                </p>
                {filteredOptions.map((option) => (
                  <OptionRowWithCalculation
                    key={option.code}
                    option={option}
                    planSubsidies={planSubsidies}
                    optionSubsidies={optionSubsidies}
                    contract={contract ?? "none"}
                    fiscalYear={fiscalYear}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <ReservationGuideDialog
        open={open}
        onOpenChange={setOpen}
        reservationUrl={reservationUrl}
        url={url}
        reservationTel={reservationTel}
        reservationFormUrl={reservationFormUrl}
      />
    </>
  );
};
